/** CSV export button component for rate plans. */
import React, { useState } from "react";
import {
  Button,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Box,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  Download as DownloadIcon,
  ArrowDropDown as ArrowDropDownIcon,
} from "@mui/icons-material";

/** Props for CSVExportButton. */
interface CSVExportButtonProps {
  /** Rate plan ID to export */
  ratePlanId: string;
  /** Rate plan name for filename */
  ratePlanName: string;
  /** Disabled state */
  disabled?: boolean;
}

/** CSV export button component. */
export default function CSVExportButton({
  ratePlanId,
  ratePlanName,
  disabled = false,
}: CSVExportButtonProps) {
  const [includeHeaders, setIncludeHeaders] = useState(true);
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleExport = async () => {
    setLoading(true);
    handleClose();

    try {
      const response = await fetch(
        `/api/v1/rate-plans/${ratePlanId}/export?include_headers=${includeHeaders}`
      );

      if (!response.ok) {
        throw new Error("Export failed");
      }

      // Get filename from Content-Disposition header or generate one
      const contentDisposition = response.headers.get("Content-Disposition");
      let filename = `rate-plan-${ratePlanName.replace(/\s+/g, "_")}-${new Date().toISOString().split("T")[0]}.csv`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error("Export failed:", err);
      // Could show error toast here
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={loading ? <CircularProgress size={20} /> : <DownloadIcon />}
        onClick={handleClick}
        disabled={disabled || loading}
        endIcon={<ArrowDropDownIcon />}
      >
        Export CSV
      </Button>

      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <Box sx={{ px: 2, py: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={includeHeaders}
                onChange={(e) => setIncludeHeaders(e.target.checked)}
                size="small"
              />
            }
            label="Include metadata headers"
          />
        </Box>
        <MenuItem onClick={handleExport} disabled={loading}>
          Download CSV
        </MenuItem>
      </Menu>
    </>
  );
}
